import { TermOrCondition, TrackerEventPriority } from "@/api/SolarCloudApi";
import { Button } from "@/components/ui/Button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { useUpdateTermOrCondition } from "@/queries/termsAndConditions/useUpdateTermOrCondition";
import { Label } from "@/components/ui/label";
import { cn } from "@/util/cn";
import { Checkbox } from "@/components/Checkbox";
import { useState, useEffect } from "react";
import { Link } from "react-router";
import { useTrackerEvents } from "@/queries/tracker/useTrackerEvents";
import PanelCard from "@/components/PanelCard";

type TermsAndConditionsProps = {
  termsAndConditions: TermOrCondition[];
};

export default function TermsAndConditions({
  termsAndConditions,
}: TermsAndConditionsProps) {
  const { mutate: updateTermOrCondition } = useUpdateTermOrCondition({});
  const { data: trackerEvents } = useTrackerEvents();

  const allTermsAccepted = termsAndConditions.every((term) => term.accepted);

  // Check for UserAllocations events with critical or high priority
  const hasUserAllocationsEvent = trackerEvents?.some(
    (event) =>
      event.type === "UserAllocations" &&
      (event.priority === TrackerEventPriority.CRITICAL ||
        event.priority === TrackerEventPriority.HIGH)
  );

  const continueLink = hasUserAllocationsEvent
    ? "/dashboard/allocations/electricity"
    : "/dashboard/";
  const [accordionValue, setAccordionValue] = useState<string>(
    allTermsAccepted ? "" : "terms"
  );

  // Collapse accordion when all terms are accepted
  useEffect(() => {
    if (allTermsAccepted) {
      setAccordionValue("");
      // Find the scrollable parent container and scroll to top
      const scrollableParent = document.querySelector(
        ".h-screen.overflow-scroll"
      );
      if (scrollableParent) {
        scrollableParent.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [allTermsAccepted]);

  const handleAccordionChange = (value: string) => {
    // Only allow collapsing if all terms are accepted
    if (value === "" && !allTermsAccepted) {
      return; // Prevent collapse when terms are not all accepted
    }
    setAccordionValue(value);
  };

  const downloadPdf = async (term: TermOrCondition) => {
    const url = term.downloadUrl!;
    const names = url.split("/");
    const name = names[names.length - 1];

    const anchor = document.createElement("a");
    anchor.download = name;
    anchor.target = "_blank";
    anchor.href = url;
    try {
      document.body.append(anchor);

      // logger.log(`@@@@ anchor: ${anchor}`);

      anchor.click();
    } finally {
      document.body.removeChild(anchor);
    }
  };

  const handleToggleTerm = (term: TermOrCondition) => {
    const updatedTerm = {
      ...term,
      accepted: !term.accepted,
    };

    updateTermOrCondition(updatedTerm);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PanelCard title="Terms and Conditions">
        <p className="mb-4">
          But this is where the lawyers step in. We just need you to agree to
          some important product and financial terms and conditions. In lay-mans
          terms we legally can not transfer any money back to you to reduce your
          energy bill 'enough though you own the panel(s)' unless you read the
          below and tick all the boxes.
        </p>
        <Accordion
          type="single"
          collapsible
          value={accordionValue}
          onValueChange={handleAccordionChange}
        >
          <AccordionItem value="terms">
            <AccordionTrigger
              className={cn(
                !allTermsAccepted &&
                  "cursor-default hover:no-underline [&>svg]:hidden"
              )}
            >
              {allTermsAccepted
                ? "All Terms and Conditions Accepted"
                : "Please review and accept all items"}
            </AccordionTrigger>
            <AccordionContent>
              <div className="divide-y divide-brand-neutral-800">
                {termsAndConditions.map((term) => (
                  <div
                    key={term.uuid}
                    className="flex flex-col-reverse sm:flex-row items-center sm:items-start gap-4 py-4"
                  >
                    <Checkbox
                      text="Tick me"
                      checkedText="Accepted"
                      checked={term.accepted}
                      onChange={() => handleToggleTerm(term)}
                      className="w-36"
                    />
                    <div className="flex flex-col items-center my-4 sm:my-0">
                      <Label
                        htmlFor={`term-${term.uuid}`}
                        className="font-normal"
                      >
                        {term.text}
                      </Label>
                      {term.downloadUrl && (
                        <Button
                          onClick={() => downloadPdf(term)}
                          className="ml-auto"
                        >
                          Download
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {allTermsAccepted && (
          <Link to={continueLink}>
            <Button className="mt-4 w-full">Proceed</Button>
          </Link>
        )}
      </PanelCard>
    </div>
  );
}
